import React, { useEffect, useMemo, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Survey } from "survey-react-ui";
import { Model, settings } from "survey-core";
import {
  confirmActionAsyncFunc,
} from "../../../components/surveyjs/survey/survey.util";
import { initSurvey } from "../../../components/surveyjs/survey/init-survey";
import { cloneDeep, get } from "lodash";
import { formatRegionDisplay } from "../../../../../utils/common-utils";
import { getFullRegionList } from "../../../../common/network/network";
import { BaseTemplate } from "./template";
import PHKLPageContainer from "../../../../../components/phkl-page-container/phkl-page-container";
import i18next from "i18next";
import { ROOT_PATH } from "../../../../../routes/constants";
import { validateUser } from "../../../network/network";
import { PruToast } from "../../../../../components/pru-toast";

const elements = BaseTemplate.pages[0].elements;

export const ForgetPasswordScreen = () => {
  initSurvey();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [formValues, setForm] = useState({} as any);

  const [pageInitiating, setPageInitiating] = useState(false);

  useEffect(() => {
    Promise.all([getFullRegionList()])
      .then((reses: any) => {
        const [regionConfigs] = reses;
        const regionPhoneField = BaseTemplate.pages[0].elements.find((item: any) => item.name === "regionPhone");
        if (regionPhoneField?.templateElements?.[0] && regionConfigs) {
          const regionOpts = Object.values(regionConfigs).map((region: any) => ({
            text: formatRegionDisplay(region.code, region.displayName),
            key: region.code,
            value: region.code,
            reg: region.regex,
          }));
          // @ts-ignore
          regionPhoneField.templateElements[0].choices = regionOpts;
          setForm({
            regionCode: regionOpts?.[0].key ?? "+852"
          });
        } else {
          setForm({
            regionCode: "+852"
          });
        }
      })
      .finally(() => {
        setPageInitiating(false);
      });
  }, []);


  const survey = useMemo(() => {
    if (pageInitiating) return null;

    const _template = cloneDeep(BaseTemplate);
    convertTemplate(_template);

    const survey = new Model(_template);
    survey.showPageTitles = false;
    survey.showNavigationButtons = "none";
    survey.questionErrorLocation = "bottom";
    survey.showCompletedPage = false;

    const defaultValue = {
      regionPhone: [
        {
          regionCode: formValues.regionCode ?? "",
          phoneNumber: formValues.phoneNumber,
        },
      ],
    };
    survey.mergeData(defaultValue);
    survey.onComplete.add((sender, options) => { });
    settings.confirmActionAsync = confirmActionAsyncFunc;
    return survey;
  }, [formValues, pageInitiating, i18next.language]);

  const handleOnConfirm = async () => {
    if (!survey) return null;
    if (!survey.validateCurrentPage()) {
      return;
    }
    const surveyData = survey.data;
    const userId = surveyData.userId;
    const phone = `${surveyData.regionPhone[0].regionCode} ${surveyData.regionPhone[0].phoneNumber}`;

    try {
      await validateUser({ userId, phone });

      navigate(`${ROOT_PATH}/onboarding/otp-verification`, {
        state: {
          isForgetPassword: true,
          userName: surveyData.userId,
          phoneNum: `${surveyData.regionPhone[0].regionCode} ${surveyData.regionPhone[0].phoneNumber}`,
        },
      });
    } catch (err) {
      const code = get(err, "code", undefined);
      if (code && code === "INVALID_USER") {
        PruToast({ message: t("Common.reset_password_incorrect_userid_or_phone") });
      } else {
        PruToast({ message: t("Common.server_error") });
      }
    };
  };

  return (
    <PHKLPageContainer title={t("Common.reset_password")}>
      {!pageInitiating && (
        <div className="flex flex-1 flex-col max-w-md px-6">
          <div className="flex flex-col w-full pb-4">
            <div className="bg-white w-full rounded-tr-3xl h-10"></div>
            <Survey
              currentPageNo={0}
              className="survey-common"
              id="create-account"
              model={survey}
            />
          </div>
          <div className=" w-full bg-white pb-7 flex items-center justify-between gap-4">
            <button
              className="bg-red-500 flex-1 text-white font-semibold py-3 rounded-lg shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75 disabled:bg-[#CCCCCC] disabled:shadow-none"
              onClick={handleOnConfirm}
            >
              {t("app.button.next")}  {/* PHKL Custom */}
            </button>
          </div>
        </div>
      )}
    </PHKLPageContainer>
  );
};

const convertTemplate = (template: any) => {
  // don't mutate the original template
  // const template: any = cloneDeep(BaseTemplate);

  // Function to translate a given field if it exists
  const translateField = (obj: any, field: string) => {
    if (obj?.[field]) {
      obj[field] = getI18n().t(obj[field]);
    }
  };

  // Loop through each element in the first page
  template.pages[0].elements = template.pages[0].elements.map((q: any) => {
    // Translate title, labelTrue, labelFalse, requiredErrorText fields
    translateField(q, "title");
    translateField(q, "placeholder");
    translateField(q, "labelTrue");
    translateField(q, "labelFalse");
    translateField(q, "requiredErrorText");
    translateField(q, "tooltips");
    translateField(q, "hint");
    translateField(q?.modalConfig, "modalTitle");

    // translate choices if they exist
    if (q.choices && Array.isArray(q.choices)) {
      q.choices.forEach((choice: any) => {
        translateField(choice, "text");
      });
    }

    // Check if the element has validators and translate their text fields
    if (q.validators && Array.isArray(q.validators)) {
      q.validators.forEach((validator: any) => {
        translateField(validator, "text");
      });
    }

    // If the element is a paneldynamic type, we need to handle nested templateElements
    if (q.type === "paneldynamic" && q.templateElements && Array.isArray(q.templateElements)) {
      q.templateElements = q.templateElements.map((te: any) => {
        translateField(te, "title");
        translateField(te, "labelTrue");
        translateField(te, "labelFalse");
        translateField(te, "requiredErrorText");
        translateField(te, "placeholder");
        translateField(te?.modalConfig, "modalTitle");

        if (te.validators && Array.isArray(te.validators)) {
          te.validators.forEach((validator: any) => {
            translateField(validator, "text");
          });
        }

        return te;
      });
    }

    return q;
  });
  return template;
};
